/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import cx from 'classnames';
import React, { Component, ReactNode, RefObject } from 'react';
import { accessibilityHelper } from 'helpers';
import { Box, ButtonClose } from 'components/common';
import { isNil } from 'lodash/fp';
import './sidebar.scss';

export const SIDEBAR_CONTAINER_CLASSNAME = 'sidebar__container';

export interface SidebarProps {
  open: boolean;
  children: ReactNode;
  className?: string;
  onClose: Function;
  onAfterClose?: Function;
  stickyElement?: ReactNode;
  requireSave?: boolean;
  lastEnabledElement?: number;
  sidebarProps?: Function;
  hideCloseButton?: boolean;
  preventAutoPending?: boolean;
}

interface SidebarState {
  focusableElements: NodeListOf<HTMLElement> | null;
  sidebarFocused: boolean;
  saveEnabled: boolean;
  isAnimating: boolean;
}

class Sidebar extends Component<SidebarProps, SidebarState> {
  sidebarRef: RefObject<HTMLDivElement>;
  private focusTimerId: number | null = null;
  private triggerElement: HTMLElement | null = null;
  private restoreFocusFrameId: number | null = null;
  static displayName = 'Sidebar';

  constructor(props) {
    super(props);
    this.state = { focusableElements: null, sidebarFocused: false, saveEnabled: false, isAnimating: false };
    this.sidebarRef = React.createRef();
  }

  componentDidMount(): void {
    const container = this.sidebarRef.current?.querySelector(`.${SIDEBAR_CONTAINER_CLASSNAME}`) as HTMLElement | null;
    if (container) {
      container.addEventListener('transitionend', (event) => {
        if (event.propertyName !== 'transform') return;
        if (!this.props.open && this.props.onAfterClose) {
          this.props.onAfterClose();
        }
        this.setState({ isAnimating: false });
      });
    }
    if (this.props.open) {
      this.refreshFocusableElementsAndFocus();
    }
  }

  getSnapshotBeforeUpdate(prevProps: Readonly<SidebarProps>): HTMLElement | null {
    if (!prevProps.open && this.props.open) {
      return document.activeElement as HTMLElement | null;
    }
    return null;
  }

  componentDidUpdate(prevProps: Readonly<SidebarProps>, _prevState: Readonly<SidebarState>, snapshot: HTMLElement | null) {
    if (prevProps.open !== this.props.open) {
      if (this.props.open) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
        document.body.style.overflowY = 'hidden';

        this.triggerElement = snapshot;
        const sidebarContainer = this.sidebarRef.current?.querySelector(`.${SIDEBAR_CONTAINER_CLASSNAME}`) as HTMLElement | null;
        if (sidebarContainer) {
          sidebarContainer.scrollTop = 0;
        }
        this.setState({ isAnimating: true }, () => {
          this.refreshFocusableElementsAndFocus();
        });
      } else {
        document.body.style.overflowY = '';
        document.body.style.paddingRight = '';

        if (this.focusTimerId !== null) {
          window.clearTimeout(this.focusTimerId);
          this.focusTimerId = null;
        }
        this.restoreFocusToTrigger();
        this.setState({ isAnimating: true, focusableElements: null, sidebarFocused: false });
      }

      if (this.props.sidebarProps) {
        this.props.sidebarProps().setIsChartSidebarOpen(this.props.open);
      }
    }
  }

  componentWillUnmount() {
    document.body.style.overflowY = '';
    document.body.classList.remove('scroll-bounce-custom');
    if (this.focusTimerId !== null) {
      window.clearTimeout(this.focusTimerId);
      this.focusTimerId = null;
    }
    if (this.restoreFocusFrameId !== null) {
      cancelAnimationFrame(this.restoreFocusFrameId);
      this.restoreFocusFrameId = null;
    }
  }

  private restoreFocusToTrigger = () => {
    const trigger = this.triggerElement;
    this.triggerElement = null;
    if (!trigger) return;

    if (this.restoreFocusFrameId !== null) {
      cancelAnimationFrame(this.restoreFocusFrameId);
    }
    this.restoreFocusFrameId = requestAnimationFrame(() => {
      this.restoreFocusFrameId = null;
      // If the original DOM node is still in the document, focus it directly
      if (document.body.contains(trigger)) {
        trigger.focus();
        return;
      }
      // Fallback: the DOM node was replaced by a re-render, find the equivalent element
      // by matching the text content of the original trigger
      const triggerText = trigger.textContent?.trim();
      if (triggerText) {
        const candidates = document.querySelectorAll<HTMLElement>('button, a, [role="button"]');
        for (let i = 0; i < candidates.length; i++) {
          if (candidates[i].textContent?.trim() === triggerText) {
            candidates[i].focus();
            return;
          }
        }
      }
    });
  };

  private refreshFocusableElementsAndFocus = () => {
    const containerEl = this.sidebarRef.current?.querySelector(`.${SIDEBAR_CONTAINER_CLASSNAME}`);
    const focusableList = (containerEl?.querySelectorAll(accessibilityHelper.FOCUSABLE_ELEMENT_SELECTORS) as NodeListOf<HTMLElement> | undefined) ?? null;
    this.setState({ focusableElements: focusableList, sidebarFocused: false }, () => {
      this.applyInitialFocusToSidebar();
    });
  };

  handleOutsideClick = (event) => {
    if (this.state.isAnimating) return;
    if (this.props.onClose) {
      this.props.onClose(event);
    }
    this.setState({ sidebarFocused: false });
  };

  handleContainerClick = (event) => event.stopPropagation();

  applyInitialFocusToSidebar = () => {
    const { focusableElements } = this.state;
    this.setState({ saveEnabled: false });
    const firstFocusableElement = focusableElements?.[0];
    if (!firstFocusableElement) return;
    if (this.focusTimerId !== null) {
      window.clearTimeout(this.focusTimerId);
    }
    this.focusTimerId = window.setTimeout(() => {
      this.focusTimerId = null;
      firstFocusableElement.focus();
    }, 50);
    this.setState({ sidebarFocused: true });
  };

  // Set focus to top of sidebar immediately when using tab
  setFocusTop = () => {
    const { focusableElements, sidebarFocused } = this.state;
    const firstFocusableElement = focusableElements?.[0];
    this.setState({ saveEnabled: false });

    if (!sidebarFocused) {
      setTimeout(() => {
        if (firstFocusableElement) {
          firstFocusableElement.focus();
        }
      }, 500);
      this.setState({ sidebarFocused: true });
    }
  };

  // Lock focus inside sidebar when using tab
  handleKeyboardFocus = (event) => {
    const { onClose, open } = this.props;
    const { saveEnabled, isAnimating } = this.state;

    if (!isAnimating) {
      accessibilityHelper.handleEscapeKey(event, onClose);
    }

    // Must explicitly check that save is enabled, execute code, and break
    // Otherwise logic will switch back and forth between enabled and disabled multiple times
    if (open && saveEnabled) {
      this.setFocus(event);
    }
    if (open && !saveEnabled) {
      this.setFocus(event);
    }
  };

  setFocus = (event) => {
    const { focusableElements } = this.state;
    const lastFocusableElement = this.getLastFocusableElement();
    const tab = event.key === 'Tab';
    const firstFocusableElement = focusableElements?.[0];
    const currentFocusedElement = document.activeElement;

    if (tab && currentFocusedElement === lastFocusableElement) {
      event.preventDefault();
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }

    // Block exiting sidebar on tab + shift combination
    if (tab && event.shiftKey && currentFocusedElement === firstFocusableElement && lastFocusableElement) {
      lastFocusableElement.focus();
    }
  };

  getLastFocusableElement = (): HTMLElement => {
    const { lastEnabledElement = 1 } = this.props;
    const { focusableElements } = this.state;
    const saveEnabledElement = isNil(focusableElements) ? null : focusableElements[focusableElements.length - 2];
    const lastFocusableElement = isNil(focusableElements) ? null : focusableElements[focusableElements.length - lastEnabledElement];

    // when lastEnabledElement is saveEnabledElement
    if (saveEnabledElement && lastEnabledElement === 2) {
      this.setState({ saveEnabled: true });
      return saveEnabledElement;
    }
    return lastFocusableElement;
  };

  handleCloseGuarded = () => {
    if (!this.state.isAnimating && this.props.onClose) {
      this.props.onClose();
    }
  };

  render() {
    const { open, onClose, className, children, stickyElement, requireSave, hideCloseButton = false, preventAutoPending = false } = this.props;

    return (
      <div
        ref={this.sidebarRef}
        onClick={this.handleOutsideClick}
        onKeyDown={this.handleKeyboardFocus}
        className={cx('sidebar', open ? 'show' : 'hide', {
          clickable: !!onClose,
          'require-save-sidebar': requireSave
        })}
      >
        <div onClick={this.handleContainerClick} className={cx(SIDEBAR_CONTAINER_CLASSNAME, className)}>
          <div className="sidebar__content-wrapper" role="dialog" aria-modal="true" aria-label="Edit Screen">
            {!hideCloseButton && (
              <Box mt>
                <ButtonClose preventAutoPending={preventAutoPending} onClick={this.handleCloseGuarded} />
              </Box>
            )}
            {children}
          </div>
          {stickyElement}
        </div>
      </div>
    );
  }
}

export default Sidebar;
